import type { PropertyCatalog } from '@/api/core/properties';
import type {
  MediaItemKind,
  MediaMarker,
  MediaObservation,
  MediaTarget,
  MediaTargetKind,
  ObservationState,
  PublicationRecordState,
  PublicationStatus,
  SelectionRole,
} from '@/api/core/property-media';

/** 서버 코드를 운영자 말로 바꾼다. 모르는 값은 원문을 그대로 보여 준다(추측해 번역하지 않는다). */
const PURPOSE: Record<string, string> = {
  PRIMARY_IMAGE: '기본 이미지',
  THUMBNAIL: '썸네일',
  GALLERY: '갤러리',
  FLOOR_PLAN: '평면도',
  VR_TOUR: 'VR 투어',
  VIDEO: '영상',
  EXTERIOR: '외관',
  DETAIL: '상세',
  COMMON_AREA: '공용 공간',
  BUILDING_LAYOUT: '건물 배치',
  LOCATION_MAP: '위치 지도',
  FLOOR_DIRECTORY: '층 안내',
  ROOM_DIRECTORY: '객실 안내',
};
// und(언어 미지정)는 "공용"이라 쓰면 옆의 용도 "공용 공간"과 헷갈려 "공통"(모든 언어에 같이 쓰는 것)으로 쓴다.
const LANGUAGE: Record<string, string> = { ko: '한국어', en: '영어', und: '공통' };

export const TARGET_KIND_LABEL: Record<MediaTargetKind, string> = {
  PROPERTY: '지점 전체',
  PROPERTY_FLOOR: '층',
  ROOM_TYPE: '객실 유형',
  ROOM: '객실',
  SLOT: '슬롯',
};
export const ITEM_KIND_LABEL: Record<MediaItemKind, string> = {
  IMAGE: '이미지',
  VIDEO: '영상',
  VR: 'VR',
  DOCUMENT: '문서',
};
export const SELECTION_ROLE_LABEL: Record<SelectionRole, string> = {
  PROPERTY_CARD: '지점 목록',
  PROPERTY_HERO: '지점 대표',
  ROOM_TYPE_GALLERY: '객실 유형 갤러리',
  SLOT_FLOOR_PLAN: '슬롯 평면도',
  UNIT_FLOOR_PLAN: '슬롯 평면도',
  COMMON_AREA: '공용공간',
};

export const purposeLabel = (code: string) => PURPOSE[code] ?? code;
export const languageLabel = (code: string) => LANGUAGE[code] ?? code;

/**
 * 객실 사진 분류 코드 — 홈페이지가 같은 분류에 쓰는 이름과 맞춘다. 모르는 값은 원문을 그대로 보여 준다.
 * (분류명은 서버에 자유 문자열로 저장되므로 한글로 적힌 값도 그대로 통과한다.)
 */
const ROOM_IMAGE_CATEGORY: Record<string, string> = {
  overall: '전경',
  commonOverall: '공용부 전경',
  desk: '책상',
  bed: '침대',
  kitchen: '주방',
  wardrobe: '수납장',
  bathroom: '화장실',
  sharedBathroom: '공용화장실',
  trioA: 'Trio - A',
  trioB: 'Trio - B',
  trioC: 'Trio - C',
  tour: '투어룸',
  other: '그 외',
  floorDirectories: '층별 안내',
};
const roomImageCategoryLabel = (name: string) => ROOM_IMAGE_CATEGORY[name] ?? name;

/** 분류명은 null(없음)과 빈 문자열(원본이 비워 둠)을 구분해 보여 준다. 알려진 분류 코드는 풀어 쓴다. */
export function categoryLabel(name: string | null): string {
  if (name === null) return '—';
  return name === '' ? '(비어 있음)' : roomImageCategoryLabel(name);
}

/**
 * 사진 한 장의 설명 — 용도·언어·분류·위치를 운영자 말로 잇는다.
 * 언어가 정해지지 않은(und) 사진은 언어를 적지 않는다. "공용"으로 적으면 공용 공간으로 읽힌다.
 */
export function photoCaption(
  group: { purpose: string; language: string; categoryName: string | null },
  contentSlot: string | null = null,
): string {
  return [
    purposeLabel(group.purpose),
    group.language === 'und' ? null : languageLabel(group.language),
    group.categoryName ? roomImageCategoryLabel(group.categoryName) : null,
    contentSlot ? roomImageCategoryLabel(contentSlot) : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

const markerValue = (marker: MediaMarker): string => {
  if (marker.state === 'NULL') return '비어 있음';
  if (marker.state === 'MISSING') return '기록 없음';
  if (marker.value === true) return '예';
  if (marker.value === false) return '아니요';
  return String(marker.value);
};

const isMarkerless = (observation: MediaObservation) =>
  [observation.isActive, observation.isPublished, observation.deleted].every(
    (marker) => marker.state === 'MISSING',
  );

/** 첫 기록에 활성·게시·삭제 표지가 하나라도 남아 있는가 — 셋 다 기록에 없으면 줄여 보이므로 흐리게 쓸 수 있다. */
export const hasSourceMarkers = (observations: MediaObservation[]): boolean => {
  const first = observations[0];
  return first !== undefined && !isMarkerless(first);
};

/**
 * 원본 provenance에서 투영한 세 표지를 한 줄로. 기록이 없으면 null.
 * 세 표지가 모두 기록에 없으면(행마다 같은 말이 세 번 반복되므로) 한마디로 줄인다.
 */
export function observationSummary(observations: MediaObservation[]): string | null {
  const first = observations[0];
  if (!first) return null;
  const text = isMarkerless(first)
    ? '기존 활성·게시·삭제 표시 기록 없음'
    : `기존 활성 ${markerValue(first.isActive)} · 게시 ${markerValue(first.isPublished)} · 삭제 ${markerValue(first.deleted)}`;
  return observations.length > 1 ? `${text} (외 ${String(observations.length - 1)}건)` : text;
}

/** 저장된 공개 선택 header를 한 줄로 — 공개 적격성은 말하지 않는다. */
export function publicationSummary(
  status: PublicationStatus | null,
  recordState: PublicationRecordState,
): string {
  if (status === null || recordState === 'ABSENT') return '없음';
  const label = status === 'PUBLISHED' ? '게시' : '비게시';
  return recordState === 'DELETED'
    ? `삭제된 기록 (${label} 상태로 저장됨)`
    : `${label} 상태로 저장됨`;
}

const shortId = (value: string) => value.slice(0, 8);

/** 대상 이름 — 불러온 카탈로그에서 찾을 수 있으면 이름으로, 없으면 짧은 ID로. */
export function mediaTargetLabel(
  target: MediaTarget,
  catalogs: readonly (PropertyCatalog | undefined)[],
): string {
  const loaded = catalogs.filter((catalog): catalog is PropertyCatalog => catalog !== undefined);
  switch (target.kind) {
    case 'PROPERTY':
      return TARGET_KIND_LABEL.PROPERTY;
    case 'PROPERTY_FLOOR':
      return `${String(target.floor)}층`;
    case 'ROOM_TYPE': {
      const type = loaded
        .flatMap((catalog) => catalog.roomTypes)
        .find((item) => item.id === target.id);
      return type ? `객실 유형 ${type.name}` : `객실 유형 ${shortId(target.id ?? '')}`;
    }
    case 'ROOM': {
      const room = loaded.flatMap((catalog) => catalog.rooms).find((item) => item.id === target.id);
      return room ? `${String(room.roomNo)}호 객실` : `객실 ${shortId(target.id ?? '')}`;
    }
    case 'SLOT': {
      const slot = loaded.flatMap((catalog) => catalog.slots).find((item) => item.id === target.id);
      const room = slot
        ? loaded.flatMap((catalog) => catalog.rooms).find((item) => item.id === slot.roomId)
        : undefined;
      // 원본이 슬롯 번호를 비워 두면 번호 대신 짧은 ID로 구분한다.
      if (slot && room)
        return `${String(room.roomNo)}호 슬롯 ${slot.slotNo.trim() || shortId(slot.id)}`;
      return `슬롯 ${shortId(target.id ?? '')}`;
    }
  }
}

export const OBSERVATION_STATE_LABEL: Record<ObservationState, string> = {
  CURRENT: '현재',
  ABSENT: '없음',
  QUARANTINED: '격리됨',
};
